/* istanbul ignore file */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, combineLatest, filter, takeUntil, switchMap } from 'rxjs';
import { PreInitSettingsService } from 'src/app/core/services/settings/pre-init-setting.service';
import { ISignupResult } from 'src/app/model/account/ISignupResult';
import { ISignupSecondaryResult } from 'src/app/model/account/ISignupSecondaryResult';
import { ISelectedContract, LoggedInUserSettings } from './settings/logged-in-user-settings';
import { ElitaChildCertificateResponseDto } from 'src/app/model/account/elita-child-certificate-response.dto';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { ADPResourceService } from 'digital-core';
import { isEmpty as _isEmpty } from 'lodash';
import { IFamilyMember } from 'src/app/model/contract/IContract';
import { AccountSignUpDto } from '../../model/account/account-signup.dto';
import { DeviceInventoryService } from './device-inventory.service';
import { ContractDeviceInventories } from './contract-device-inventories';
import { SearchContractRequest, SendOTPRequest, ValidateOTPRequest, VerifyOTPRequest } from 'src/app/model/account/user-otp-request';
import { LookupContractResponse } from 'src/app/model/account/lookup-contract-response.dto';
import { ValidatedOTPResponse } from 'src/app/model/account/validated-otp-response';
import { PreferenceConsent } from 'src/app/model/account/preference-center-request.dto';
import { UpdateCustomerAddressRequest } from 'src/app/model/account/update-customer-address-request.dto';


const USE_MOCK = false;

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  middlewareUrl: string;
  accountPath: string;
  tenantId: string;
  isElita: string;
  sfSite: string;
  sfProvider: string;
  companyCode: string;
  clientName: string;
  selectedContract: ISelectedContract;
  private dealerIds: string = null;
  private isUserPrimary: boolean;

  private colors: string[] = [];
  private assignedColors: { [initial: string]: string } = {};
  constructor(
    private http: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private loggedInUserSettings: LoggedInUserSettings,
    public resourceService: ADPResourceService,
    private deviceInventoryService: DeviceInventoryService,
  ) {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare.accountPath;
    this.tenantId = USE_MOCK ? 'MOCK' : (this.resourceService.getPortalSetting('Global_CTC_TenantId')?.Value ?? '');
    this.isElita = this.resourceService.getPortalSetting('Global_CTC_IsElita')?.Value ?? '';
    this.dealerIds = this.resourceService.getPortalSetting('Global_DealerCodes')?.Value ?? '';
    this.sfSite = this.preInitSettingsService.getSiteSettings()?.id ?? '';
    this.sfProvider = this.preInitSettingsService.getSiteSettings()?.provider ?? '';
    this.companyCode = this.resourceService.getPortalSetting('Global_CTC_CompanyCode')?.Value ?? '';
    this.clientName = this.resourceService.getPortalSetting('Global_SourceClientName')?.Value ?? '';
    this.getContractAndUserClaims();
  }

  getSignup(contractId: string, zipCode: string, groupId: string, reCaptchaValue = ''): Observable<ISignupResult> {
    const httpOptions = {
      params: new HttpParams()
        .set('tenantId', this.tenantId)
        .set('contractId', contractId)
        .set('zipcode', zipCode)
        .set('groupId', groupId)
        .set('dealerIds', this.dealerIds)
        .set('reCaptchaValue', reCaptchaValue)
        .set('oktaPrefix', this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value)
    };
    return this.http.get<ISignupResult>(`${this.middlewareUrl}${this.accountPath}signup?isElitaActive=${this.isElita}`, httpOptions);
  }

  getSecondarySignup(guid: string, groupId: string): Observable<ISignupSecondaryResult> {
    const httpOptions = {
      params: new HttpParams()
        .set('Code', guid)
        .set('groupId', groupId)
        .set('companyCode', this.companyCode)
        .set('oktaPrefix', this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value)
    };

    return this.http.get<ISignupSecondaryResult>(
      `${this.middlewareUrl}${this.accountPath}signupsecondary`, httpOptions);
  }

  /**
   * This is for the old account creation / sign up flow (the one that existed pre May 2024)
   * @param contractId
   * @param password
   * @param mobileNumber
   * @param avatarUrl
   * @param isGroupSetup
   * @returns
   */
  postSignup(contractId: string, password: string, mobileNumber: string, avatarUrl: string, isGroupSetup = false) {
    const group = this.loggedInUserSettings.getCurrentGroup();
    const body = {
      TenantId: this.tenantId,
      ContractId: contractId,
      Password: password,
      PhoneNumber: mobileNumber,
      AvatarUrl: avatarUrl,
      GroupId: group.id
    };
    return this.http.post(`${this.middlewareUrl}${this.accountPath}signup?oktaPrefix=${this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value}&isElitaActive=${this.isElita}&contractId=${contractId}&isGroupSetup=${isGroupSetup}&dealerIds=${encodeURIComponent(this.dealerIds)}&companyCode=${this.companyCode}`, body);
  }

  postSignupSecondary(inviteId: string, password: string, mobileNumber: string, firstName: string, lastName: string) {
    const group = this.loggedInUserSettings.getCurrentGroup();
    const body = {
      InviteId: inviteId,
      Password: password,
      PhoneNumber: mobileNumber,
      AvatarUrl: '',
      Relationship: null,
      FirstName: firstName,
      LastName: lastName,
      GroupId: group.id
    };
    return this.http.post(`${this.middlewareUrl}${this.accountPath}signupsecondary?oktaPrefix=${this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value}&isElitaActive=${this.isElita}&sf_site=${this.sfSite}&sf_provider=${this.sfProvider}&companyCode=${this.companyCode}&dealerIds=${encodeURIComponent(this.dealerIds)}`, body);
  }

  postSignupSecondaryOktaExists(inviteId: string, mobileNumber: string, firstName: string, lastName: string) {
    const group = this.loggedInUserSettings.getCurrentGroup();
    const body = {
      InviteId: inviteId,
      PhoneNumber: mobileNumber,
      Relationship: null,
      FirstName: firstName,
      LastName: lastName,
      GroupId: group.id
    };
    return this.http.post(`${this.middlewareUrl}${this.accountPath}signupsecondaryoktaexists?oktaPrefix=${this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value}&isElitaActive=${this.isElita}&sf_site=${this.sfSite}&sf_provider=${this.sfProvider}&companyCode=${this.companyCode}&dealerIds=${encodeURIComponent(this.dealerIds)}`, body);
  }

  /**
   * This is for the new account creation / signup flow (the one built in May 2024).
   *
   * @param signupInfo
   * @returns
   */
  public signUpNewAccount(signupInfo: AccountSignUpDto) {
    const headers = new HttpHeaders({
      ClientDomain: `${window.location.protocol}//${window.location.hostname}`,
    });
    return this.http.post<string>(`${this.middlewareUrl}${this.accountPath}account-signup`, signupInfo, { headers });
  }

  postUpdatePassword(emailAddress: string, currentPassword: string, newPassword: string) {
    return this.http.post(`${this.middlewareUrl}${this.accountPath}changepassword`, {
      emailAddress,
      currentPassword,
      newPassword
    });
  }

  putEditMemberDetails(contract: string, customer: string, data: any) {
    return this.http.put(`${this.middlewareUrl}${this.accountPath}memberdetails?isUserPrimary=${this.isUserPrimary}&isElitaActive=${this.isElita}&companyCode=${this.companyCode}&dealerIds=${encodeURIComponent(this.dealerIds)}`, {
      Contract: contract,
      Customer: customer,
      ProfileData: data
    });
  }

  /**
   * Saves the user's communication preferences (Preference Center screen).
   *
   * Targets the account-level Elita endpoint:
   * `…/homewarranty-policy/{programCode}/portalAccounts/{accountIdentifier}/consents`
   *
   * Two pieces of upstream context — `programCode` and `accountIdentifier`
   * — are resolved server-side by the middleware, NOT sent by the FE:
   *
   * - `programCode` comes from the middleware's own portal-config mapping
   *   (it already knows which portal client is calling and the matching
   *   Elita dealer/program code). Sending it from the FE would just be a
   *   second source of truth that can drift.
   * - `accountIdentifier` is extracted from the caller's Okta auth token
   *   (sub/email claim). Not sending it in the URL keeps the user's email
   *   out of access logs, browser history, and proxy logs.
   */
  putPreferenceCenter(consents: PreferenceConsent[]): Observable<unknown> {
    const url =
      `${this.middlewareUrl}${this.accountPath}consents` +
      `?isElitaActive=${this.isElita}` +
      `&companyCode=${this.companyCode}` +
      `&dealerIds=${encodeURIComponent(this.dealerIds)}`;
    return this.http.put(url, { consents });
  }

  putEditMemberAvatar(contractId: string, memberEmail: string, avatarUrl: string, avatarColor: string, isEprismOnly?: string) {
    return this.http.put(`${this.middlewareUrl}${this.accountPath}memberavatar?isUserPrimary=${this.isUserPrimary}&isEprismOnly=${isEprismOnly}`, {
      ContractId: contractId,
      MemberEmail: memberEmail,
      AvatarUrl: avatarUrl,
      ProfileColor: avatarColor
    });
  }

  public assignColors(users: IFamilyMember[]) {

    // user structure used for the color picker
    //users: { name: string, initials: string, color: string }[]

    // get list of available colors to choose from
    this.colors = this.resourceService.getPortalSetting('Global_Avatar_Colors').Value.split(',');

    users.forEach(user => {
      const initial = this.getInitials(user.firstName + ' ' + user.lastName, 2);

      if (user.profileColor === null || user.profileColor === undefined || user.profileColor === '') {
        if (!this.assignedColors[initial]) {
          const colorIndex = (initial.charCodeAt(0) + initial.charCodeAt(1)) % this.colors.length;
          this.assignedColors[initial] = this.colors[colorIndex];
          user.profileColor = this.colors[colorIndex];
        } else {
          let colorIndex = this.colors.indexOf(this.assignedColors[initial]) + 1;
          if (colorIndex >= this.colors.length) {
            colorIndex = 0;
          }
          while (Object.values(this.assignedColors).includes(this.colors[colorIndex])
            && Object.keys(this.assignedColors).length < this.colors.length) {
            colorIndex = (colorIndex + 1) % this.colors.length;
          }
          this.assignedColors[initial] = this.colors[colorIndex];
          user.profileColor = this.colors[colorIndex];
        }
      } else {
        this.assignedColors[initial] = user.profileColor;
      }
    });
  }

  private getInitials(name: string, size: number): string {
    name = name.trim();

    if (!name) {
      return '';
    }

    const initials = name.split(' ');

    if (size && size < initials.length) {
      return this.constructInitials(initials.slice(0, size));
    } else {
      return this.constructInitials(initials);
    }
  }

  /**
   * Iterates a person's name string to get the initials of each word in uppercase.
   */
  private constructInitials(elements: string[]): string {

    if (!elements || !elements.length) {
      return '';
    }

    return elements
      .filter(element => element && element.length > 0)
      .map(element => element[0].toUpperCase())
      .join('');
  }

  deleteFamilyMember(email: string, contractId: string) {
    return this.http.put(`${this.middlewareUrl}${this.accountPath}deletemember?isElitaActive=${this.isElita}&email=${encodeURIComponent(email)}&contractId=${contractId}&companyCode=${this.companyCode}&dealerIds=${encodeURIComponent(this.dealerIds)}`, {});
  }

  reactivateFamilyMember(email: string, contractId: string) {
    return this.http.put(`${this.middlewareUrl}${this.accountPath}familymember?email=${encodeURIComponent(email)}&contractId=${contractId}&dealerIds=${encodeURIComponent(this.dealerIds)}`, {});
  }

  isUserLoggedInFirstTime() {
    return this.http.get<boolean>(`${this.middlewareUrl}${this.accountPath}isuserloggedinfirsttime`);
  }

  isUserLockedOut(email: string) {
    return this.http.get<number>(`${this.middlewareUrl}${this.accountPath}IsUserLockedOut?email=`+encodeURIComponent(email));
  }

  getElitaChildCertificateNumber(certificateNumber: string, childCertificateNumber: string, dealerCode: string): Observable<ElitaChildCertificateResponseDto> {
    // This implementation only applies to TMO where dealer code is appended to cert
    if (/^\d/.test(certificateNumber)) {
      certificateNumber = dealerCode + certificateNumber;
      childCertificateNumber = dealerCode + childCertificateNumber + "-1";
    }
    const body = {
      clientName: this.clientName,
      certificateNumber: certificateNumber,
      childCertificateNumber: childCertificateNumber,
      dealerCode: dealerCode
    };
    return this.http.post<ElitaChildCertificateResponseDto>(`${this.middlewareUrl}${this.accountPath}get-elita-child-certificate-number`, body);
  }

  private getContractAndUserClaims() {
    combineLatest([
      this.loggedInUserSettings.$selectedContract,
      this.loggedInUserSettings.$userClaims
    ]).pipe(
      filter(([a, b]) => a !== null && b !== null)
    ).subscribe(([contract, user]: [ISelectedContract, UserClaims]) => {
      this.isUserPrimary = this.googleAnalyticsService.isUserPrimary(contract, user);
      this.selectedContract = contract;
    });
  }

  getCarriers(countryIso: string = 'US'): Observable<any> {
    return this.http.get(`${this.middlewareUrl}${this.accountPath}carriers/${countryIso}`);
  }

  accountLookup(payload: SearchContractRequest): Observable<LookupContractResponse> {
    return this.http.post<LookupContractResponse>(`${this.middlewareUrl}${this.accountPath}accountLookup`, payload);
  }

  checkOktaExists(email: string) {
    const oktaPrefix = this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value;
    return this.http.get(`${this.middlewareUrl}${this.accountPath}checkOktaExists?oktaPrefix=${oktaPrefix}&email=${encodeURIComponent(email)}`);
  }

  sendOTPCode(payload: SendOTPRequest) {
    const oktaPrefix = this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value;
    return this.http.post(`${this.middlewareUrl}${this.accountPath}createOTP?isElitaActive=${this.isElita}&dealerIds=${encodeURIComponent(this.dealerIds)}&companyCode=${this.companyCode}&oktaPrefix=${oktaPrefix}`, payload);
  }

  validateOTP(payload: VerifyOTPRequest): Observable<ValidatedOTPResponse> {
    return this.http.post<ValidatedOTPResponse>(`${this.middlewareUrl}${this.accountPath}validateOTP?&dealerIds=${encodeURIComponent(this.dealerIds)}&companyCode=${this.companyCode}`, payload);
  }

  validateAccount(payload: ValidateOTPRequest): Observable<void> {
    return this.http.post<void>(`${this.middlewareUrl}${this.accountPath}validate-account`, payload);
  }

  updateCustomerAddress(payload: UpdateCustomerAddressRequest): Observable<void> {
    return this.http.post<void>(`${this.middlewareUrl}${this.accountPath}update-customer-address`, payload);
  }

  getAllInventoriesSelectedContract(takeUntilObservable$: Observable<any>, useCache?: boolean): Observable<ContractDeviceInventories> {
    const programCode = this.resourceService.getPortalSetting("Global_ProgramCode").Value;

    return this.loggedInUserSettings.$selectedContract.pipe(
      switchMap(contract => new Observable<ContractDeviceInventories>(observer => {
        this.deviceInventoryService.getElitaPolicyInfo(contract.id, contract.dealerId, useCache).subscribe({
          next: certsArr => {
            let contractDeviceInventries: ContractDeviceInventories = { masterCertificateNumber: contract.id };
            if (contract.certificateNumberForPhonesPolicy) {
              let phoneCertInfo = certsArr.find(cert => cert.certificateNumber === contract.certificateNumberForPhonesPolicy);
              contractDeviceInventries.cellularEnabledPolicyInventory = this.deviceInventoryService.constructInventoryFromCertDetails(phoneCertInfo, contract, programCode);
            }
            if (contract.certificateNumberForGadgetsPolicy) {
              let gadgetCertInfo = certsArr.find(cert => cert.certificateNumber === contract.certificateNumberForGadgetsPolicy);
              contractDeviceInventries.gadgetPolicyInventory = this.deviceInventoryService.constructInventoryFromCertDetails(gadgetCertInfo, contract, programCode);
            }
            if (contract.certificateNumberForClientSoldGadgetsPolicy) {
              let clientSoldGadgetCertInfo = certsArr.find(cert => cert.certificateNumber === contract.certificateNumberForClientSoldGadgetsPolicy);
              contractDeviceInventries.gadgetClientSoldPolicyInventory = this.deviceInventoryService.constructInventoryFromCertDetails(clientSoldGadgetCertInfo, contract, programCode);
            }

            observer.next(contractDeviceInventries);
            observer.complete();
          },
          error: err => {
            console.error('Error in getAllInventoriesSelectedContract:', err);
            observer.error(err);
          }
        });
      })),
      takeUntil(takeUntilObservable$)
    );
  }
  

  


  
}
